const MOBILE_QUERY = '(max-width: 767px)';
const DISMISS_COOKIE = 'app_banner_dismissed';
const DISMISS_COOKIE_MAX_AGE = 2147483646;
const PROMO_STACK_INNER = '.mobile-promo-banners__inner';
const LAYOUT_MANAGED_CLASS = 'promo-banners-layout-managed';

const isMobile = () => window.matchMedia(MOBILE_QUERY).matches;

function isEmbeddedDocument() {
  try {
    return window.self !== window.top;
  } catch (error) {
    return true;
  }
}

function shouldApplyPromoBannerLayout() {
  return (
    !isEmbeddedDocument() && !document.body.classList.contains('iframe_body')
  );
}

let appBannerIsShown = false;
let promoStackNaturalHeight = 0;

function getPromoStack() {
  return document.getElementById('mobile-promo-banners');
}

function getAppBanner() {
  return document.getElementById('app-install-banner');
}

function getUtilityBanner() {
  return document.getElementById('utility-banner');
}

function getPromoStackInner() {
  return getPromoStack()?.querySelector(PROMO_STACK_INNER) ?? null;
}

function hasPromoBanners() {
  return appBannerIsShown || Boolean(getUtilityBanner());
}

function getVisiblePromoBanners() {
  const banners = [];
  const appBanner = getAppBanner();

  if (appBanner && appBannerIsShown) {
    banners.push(appBanner);
  }

  const utilityBanner = getUtilityBanner();

  if (utilityBanner) {
    banners.push(utilityBanner);
  }

  return banners;
}

function measurePromoStackHeight() {
  const stackInner = getPromoStackInner();

  if (!stackInner) {
    return 0;
  }

  return stackInner.scrollHeight || stackInner.offsetHeight || 0;
}

function syncPromoStackOffset(height) {
  const container = document.querySelector('body > .container');

  if (!container) {
    return;
  }

  // Always set inline margin so we override the server-rendered fallback rule in
  // mobile-promo-banners.blade.php once promo-banners-layout-managed is active.
  container.style.marginTop = height > 0 ? `${height}px` : '0px';
}

function activatePromoBannerLayoutManagement() {
  document.body.classList.add(LAYOUT_MANAGED_CLASS);
}

function cachePromoStackHeight() {
  if (!isMobile() || !hasPromoBanners()) {
    promoStackNaturalHeight = 0;
    syncPromoStackOffset(0);
    return 0;
  }

  const total = measurePromoStackHeight();

  if (total > 0) {
    promoStackNaturalHeight = total;
  }

  syncPromoStackOffset(promoStackNaturalHeight);

  return promoStackNaturalHeight;
}

function getScrollProgress() {
  if (promoStackNaturalHeight <= 0) {
    return 0;
  }

  return Math.min(Math.max(window.scrollY / promoStackNaturalHeight, 0), 1);
}

function clearPromoStackScrollStyles() {
  const stackInner = getPromoStackInner();
  const stack = getPromoStack();

  if (stackInner) {
    stackInner.style.removeProperty('transform');
  }

  if (stack) {
    stack.style.removeProperty('max-height');
    stack.style.removeProperty('overflow');
    stack.style.removeProperty('pointer-events');
  }
}

function resetScrollLinkedLayout() {
  clearPromoStackScrollStyles();
  syncPromoStackOffset(0);
}

function applyScrollLinkedLayout() {
  if (!shouldApplyPromoBannerLayout() || !isMobile() || !hasPromoBanners()) {
    resetScrollLinkedLayout();
    return;
  }

  if (promoStackNaturalHeight <= 0) {
    cachePromoStackHeight();
  }

  const progress = getScrollProgress();
  const stack = getPromoStack();
  const stackInner = getPromoStackInner();
  const totalVisible = promoStackNaturalHeight * (1 - progress);
  const hiddenHeight = promoStackNaturalHeight - totalVisible;

  if (stack) {
    stack.style.maxHeight = `${totalVisible}px`;
    stack.style.overflow = 'hidden';
    stack.style.pointerEvents = progress >= 1 ? 'none' : '';
  }

  if (stackInner) {
    stackInner.style.transform = `translateY(-${hiddenHeight}px)`;
  }
}

function handleScrollLinkedLayout() {
  applyScrollLinkedLayout();
}

function setupScrollLinkedLayout() {
  let ticking = false;
  const onScroll = () => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        handleScrollLinkedLayout();
        ticking = false;
      });
      ticking = true;
    }
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.matchMedia(MOBILE_QUERY).addEventListener('change', () => {
    cachePromoStackHeight();
    handleScrollLinkedLayout();
  });

  handleScrollLinkedLayout();
}

function setupOffsetObservers() {
  const stack = getPromoStack();

  if (!stack) {
    return;
  }

  const observer = new ResizeObserver(() => {
    requestAnimationFrame(() => {
      cachePromoStackHeight();
      applyScrollLinkedLayout();
    });
  });

  observer.observe(stack);

  const inner = stack.querySelector(PROMO_STACK_INNER);

  if (inner) {
    observer.observe(inner);
  }

  getVisiblePromoBanners().forEach((banner) => {
    observer.observe(banner);
  });
}

function refreshPromoBannerLayout() {
  if (!shouldApplyPromoBannerLayout()) {
    resetScrollLinkedLayout();
    return;
  }

  cachePromoStackHeight();
  applyScrollLinkedLayout();
}

function initAppBanner() {
  const appBanner = getAppBanner();

  if (!appBanner) {
    return;
  }

  appBannerIsShown = true;

  const installIos = document.getElementById('app-banner-install-ios');
  const installAndroid = document.getElementById('app-banner-install-android');
  const closeBtn = document.getElementById('app-banner-close');

  function trackInstallClick(link) {
    window.dataLayer = window.dataLayer || [];
    const platformEvent = link.dataset.datalayerEvent;
    const allEvent = appBanner.dataset.datalayerEventAll;

    if (platformEvent) {
      window.dataLayer.push({ event: platformEvent });
    }

    if (allEvent) {
      window.dataLayer.push({ event: allEvent });
    }
  }

  installIos?.addEventListener('click', () => trackInstallClick(installIos));
  installAndroid?.addEventListener('click', () =>
    trackInstallClick(installAndroid)
  );

  closeBtn?.addEventListener('click', () => {
    document.cookie = `${DISMISS_COOKIE}=1; max-age=${DISMISS_COOKIE_MAX_AGE}; path=/`;
    appBannerIsShown = false;
    appBanner.setAttribute('hidden', '');

    const targetHeight = hasPromoBanners() ? measurePromoStackHeight() : 0;
    promoStackNaturalHeight = targetHeight;
    syncPromoStackOffset(targetHeight);
    clearPromoStackScrollStyles();
    appBanner.remove();

    if (targetHeight > 0) {
      applyScrollLinkedLayout();
    } else {
      resetScrollLinkedLayout();
    }
  });
}

function init() {
  if (!getPromoStack()) {
    return;
  }

  initAppBanner();

  if (!shouldApplyPromoBannerLayout()) {
    activatePromoBannerLayoutManagement();
    resetScrollLinkedLayout();
    return;
  }

  // Set inline margin before disabling the server CSS fallback to avoid CLS.
  cachePromoStackHeight();
  activatePromoBannerLayoutManagement();

  setupScrollLinkedLayout();
  setupOffsetObservers();
  window.addEventListener('resize', refreshPromoBannerLayout);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
